import React from 'react';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { User, Star, TrendingUp, TrendingDown, Lightbulb } from 'lucide-react';
import { theme } from '../styles/theme';
import { InitialReview } from '../types';

const ReviewContainer = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;
  box-shadow: ${theme.shadows.md};
  transition: all ${theme.transitions.base};

  &:hover {
    box-shadow: ${theme.shadows.lg};
    transform: translateY(-2px);
  }
`;

const ReviewHeader = styled.div`
  background: linear-gradient(135deg, ${theme.colors.primary[500]}, ${theme.colors.primary[600]});
  color: white;
  padding: ${theme.spacing[6]};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
    border-radius: 50%;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  z-index: 1;
`;

const ReviewerInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
`;

const ReviewerIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: ${theme.borderRadius.full};
  backdrop-filter: blur(10px);
`;

const ReviewerDetails = styled.div`
  h3 {
    margin: 0;
    font-size: ${theme.typography.fontSize.lg};
    font-weight: ${theme.typography.fontWeight.semibold};
    color: white;
  }
  
  p {
    margin: ${theme.spacing[1]} 0 0 0;
    opacity: 0.9;
    font-size: ${theme.typography.fontSize.sm};
    color: white;
  }
`;

const ScoreDisplay = styled.div`
  text-align: center;
`;

const ScoreValue = styled.div`
  font-size: ${theme.typography.fontSize['3xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  line-height: 1;
  margin-bottom: ${theme.spacing[1]};
`;

const ScoreLabel = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  opacity: 0.9;
`;

const ScoreIndicator = styled.div<{ score: number }>`
  width: 60px;
  height: 60px;
  border-radius: ${theme.borderRadius.full};
  background: conic-gradient(
    from 0deg,
    rgba(255, 255, 255, 0.3) 0deg,
    rgba(255, 255, 255, 0.8) ${({ score }) => (score / 100) * 360}deg,
    rgba(255, 255, 255, 0.1) ${({ score }) => (score / 100) * 360}deg
  );
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${theme.spacing[2]} auto;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    width: 48px;
    height: 48px;
    background: ${theme.colors.primary[500]};
    border-radius: ${theme.borderRadius.full};
  }
`;

const ReviewBody = styled.div`
  padding: ${theme.spacing[6]};
`;

const ReviewText = styled.div`
  background: ${theme.colors.backgroundSecondary};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[5]};
  font-size: ${theme.typography.fontSize.sm};
  line-height: ${theme.typography.lineHeight.relaxed};
  color: ${theme.colors.textPrimary};
  position: relative;

  p { margin: 0 0 ${theme.spacing[2]} 0; }
  p:last-child { margin-bottom: 0; }
  ul, ol { margin: 0 0 ${theme.spacing[2]} ${theme.spacing[4]}; padding: 0; }
  li { margin-bottom: ${theme.spacing[1]}; }
  strong { font-weight: ${theme.typography.fontWeight.semibold}; color: ${theme.colors.textPrimary}; }
  em { font-style: italic; }
  code {
    background: ${theme.colors.surface};
    padding: 0 ${theme.spacing[1]};
    border-radius: ${theme.borderRadius.sm};
    font-family: ${theme.typography.fontFamily.mono};
    font-size: ${theme.typography.fontSize.xs};
  }
  h1, h2, h3, h4 {
    margin: ${theme.spacing[3]} 0 ${theme.spacing[2]} 0;
    font-weight: ${theme.typography.fontWeight.semibold};
    font-size: ${theme.typography.fontSize.sm};
    &:first-child { margin-top: 0; }
  }

  &::before {
    content: '"';
    position: absolute;
    top: -10px;
    left: 15px;
    font-size: 40px;
    color: ${theme.colors.primary[300]};
    font-family: serif;
  }
`;

const InsightsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${theme.spacing[4]};
`;

const InsightCard = styled.div<{ type: 'strengths' | 'weaknesses' | 'recommendations' }>`
  background: ${({ type }) => {
    switch (type) {
      case 'strengths':
        return theme.colors.success[100];
      case 'weaknesses':
        return theme.colors.error[100];
      case 'recommendations':
        return theme.colors.warning[100];
      default:
        return theme.colors.backgroundSecondary;
    }
  }};
  border: 1px solid ${({ type }) => {
    switch (type) {
      case 'strengths':
        return theme.colors.success[500] + '40';
      case 'weaknesses':
        return theme.colors.error[500] + '40';
      case 'recommendations':
        return theme.colors.warning[500] + '40';
      default:
        return theme.colors.border;
    }
  }};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing[4]};
  transition: all ${theme.transitions.base};

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.sm};
  }
`;

const InsightHeader = styled.div<{ type: 'strengths' | 'weaknesses' | 'recommendations' }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[3]};
  
  h4 {
    margin: 0;
    font-size: ${theme.typography.fontSize.base};
    font-weight: ${theme.typography.fontWeight.semibold};
    color: ${({ type }) => {
      switch (type) {
        case 'strengths':
          return theme.colors.success[700];
        case 'weaknesses':
          return theme.colors.error[700];
        case 'recommendations':
          return theme.colors.warning[700];
        default:
          return theme.colors.textPrimary;
      }
    }};
  }
`;

const InsightIcon = styled.div<{ type: 'strengths' | 'weaknesses' | 'recommendations' }>`
  color: ${({ type }) => {
    switch (type) {
      case 'strengths':
        return theme.colors.success[600];
      case 'weaknesses':
        return theme.colors.error[600];
      case 'recommendations':
        return theme.colors.warning[600];
      default:
        return theme.colors.textSecondary;
    }
  }};
`;

const InsightList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const InsightItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[2]};
  font-size: ${theme.typography.fontSize.sm};
  line-height: ${theme.typography.lineHeight.relaxed};
  color: ${theme.colors.textSecondary};

  &:last-child {
    margin-bottom: 0;
  }

  &::before {
    content: '•';
    color: ${theme.colors.textMuted};
    font-weight: bold;
    margin-top: 2px;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[8]};
  text-align: center;
  color: ${theme.colors.textMuted};
  background: ${theme.colors.backgroundSecondary};
  border: 2px dashed ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
`;

const EmptyIcon = styled.div`
  width: 48px;
  height: 48px;
  background: ${theme.colors.primary[100]};
  border-radius: ${theme.borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${theme.spacing[3]};
  color: ${theme.colors.primary[500]};
`;

interface InitialReviewCardProps {
  review?: InitialReview;
}

const InitialReviewCard: React.FC<InitialReviewCardProps> = ({ review }) => {
  if (!review) {
    return (
      <EmptyState>
        <EmptyIcon>
          <User size={24} />
        </EmptyIcon>
        <p>AI review will appear here once initial assessment is complete...</p>
      </EmptyState>
    );
  }

  const getScoreColor = (score: number) => {
    if (score >= 80) return theme.colors.success[500];
    if (score >= 60) return theme.colors.warning[500];
    return theme.colors.error[500];
  };

  const getScoreGrade = (score: number) => {
    if (score >= 90) return 'Excellent';
    if (score >= 80) return 'Good';
    if (score >= 70) return 'Fair';
    if (score >= 60) return 'Needs Improvement';
    return 'Poor';
  };

  return (
    <ReviewContainer>
      <ReviewHeader>
        <HeaderContent>
          <ReviewerInfo>
            <ReviewerIcon>
              <User size={24} />
            </ReviewerIcon>
            <ReviewerDetails>
              <h3>AI Reviewer</h3>
              <p>Initial Assessment Complete</p>
            </ReviewerDetails>
          </ReviewerInfo>
          
          <ScoreDisplay>
            <ScoreIndicator score={review.score}>
              <Star size={20} style={{ position: 'relative', zIndex: 1, color: 'white' }} />
            </ScoreIndicator>
            <ScoreValue>{review.score}</ScoreValue>
            <ScoreLabel>{getScoreGrade(review.score)}</ScoreLabel>
          </ScoreDisplay>
        </HeaderContent>
      </ReviewHeader>

      <ReviewBody>
        <ReviewText>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {review.review || ''}
          </ReactMarkdown>
        </ReviewText>

        <InsightsGrid>
          {review.strengths && review.strengths.length > 0 && (
            <InsightCard type="strengths">
              <InsightHeader type="strengths">
                <InsightIcon type="strengths">
                  <TrendingUp size={18} />
                </InsightIcon>
                <h4>Strengths</h4>
              </InsightHeader>
              <InsightList>
                {review.strengths.map((strength, index) => (
                  <InsightItem key={index}>{strength}</InsightItem>
                ))}
              </InsightList>
            </InsightCard>
          )}

          {review.weaknesses && review.weaknesses.length > 0 && (
            <InsightCard type="weaknesses">
              <InsightHeader type="weaknesses">
                <InsightIcon type="weaknesses">
                  <TrendingDown size={18} />
                </InsightIcon>
                <h4>Areas for Improvement</h4>
              </InsightHeader>
              <InsightList>
                {review.weaknesses.map((weakness, index) => (
                  <InsightItem key={index}>{weakness}</InsightItem>
                ))}
              </InsightList>
            </InsightCard>
          )}

          {review.recommendations && review.recommendations.length > 0 && (
            <InsightCard type="recommendations">
              <InsightHeader type="recommendations">
                <InsightIcon type="recommendations">
                  <Lightbulb size={18} />
                </InsightIcon>
                <h4>Recommendations</h4>
              </InsightHeader>
              <InsightList>
                {review.recommendations.map((recommendation, index) => (
                  <InsightItem key={index}>{recommendation}</InsightItem>
                ))}
              </InsightList>
            </InsightCard>
          )}
        </InsightsGrid>
      </ReviewBody>
    </ReviewContainer>
  );
};

export default InitialReviewCard;